/**
 * TeachBud Presentation Engine
 *
 * SYNCHRONIZED INSTRUCTIONAL RESOURCE CONTRACT
 * ─────────────────────────────────────────────
 * The Presentation is one of three coordinated views of the same LessonBlueprint.
 * It transforms delivery format only — it never re-generates instructional content.
 *
 *   Lesson Plan    → AI-generated; becomes the LessonBlueprint.
 *   Presentation   → Blueprint items verbatim → formatted into slides.
 *   Activity Sheet → Blueprint items verbatim → student guided notes.
 *   Assessment     → Same skills, new numbers/scenarios (explicitly allowed).
 *
 * Every practice slide must contain the exact studentContent from its blueprint item.
 * Every reveal slide must contain the exact answer from its blueprint item.
 * Numbers, wording, names, and scenarios must NEVER be independently regenerated.
 */

// ── Slide types ───────────────────────────────────────────────────────────────

export type SlideKind =
  | 'title'
  | 'learning-target'
  | 'hook'
  | 'vocabulary'
  | 'concept'
  | 'prediction'
  | 'discuss'
  | 'reveal'
  | 'misconception'
  | 'teacher-tip'
  | 'practice'
  | 'you-try'
  | 'engagement'
  | 'exit-ticket';

export type VisualStyle =
  | 'minimal'
  | 'modern'
  | 'elementary'
  | 'middle-school'
  | 'high-school'
  | 'colorful'
  | 'dark';

export type TeachingStyle =
  | 'direct-instruction'
  | 'inquiry'
  | 'gradual-release'
  | 'workshop'
  | 'interactive';

export type QuestionStyle =
  | 'original'
  | 'multiple-choice'
  | 'think-pair-share'
  | 'turn-and-talk'
  | 'cold-call'
  | 'poll'
  | 'whiteboard-response';

export type AnswerReveal =
  | 'immediately'
  | 'after-discussion'
  | 'hidden-until-click'
  | 'teacher-only';

export type EngagementAddon =
  | 'think-pair-share'
  | 'discussion-prompt'
  | 'challenge-question'
  | 'brain-break'
  | 'partner-activity'
  | 'poll'
  | 'mini-quiz'
  | 'exit-reflection';

export type ImageMode = 'none' | 'auto-relevant' | 'ai-illustrations' | 'diagrams';

// ── Configuration ─────────────────────────────────────────────────────────────

export interface PresentationConfig {
  visualStyle: VisualStyle;
  teachingStyle: TeachingStyle;
  questionStyle: QuestionStyle;
  answerReveal: AnswerReveal;
  engagementAddons: EngagementAddon[];
  images: ImageMode;
  /**
   * Visual treatment density for slide graphics/illustrations.
   * Passed as an AI prompt hint — does NOT authorize new problems or scenarios.
   */
  graphicsDensity?: 'none' | 'minimal' | 'standard' | 'rich';
}

export function defaultPresentationConfig(): PresentationConfig {
  return {
    visualStyle: 'modern',
    teachingStyle: 'gradual-release',
    questionStyle: 'think-pair-share',
    answerReveal: 'after-discussion',
    engagementAddons: ['discussion-prompt', 'exit-reflection'],
    images: 'none',
  };
}

// ── Slide data ────────────────────────────────────────────────────────────────

export interface PresentationSlide {
  id: string;
  kind: SlideKind;
  heading: string;
  subheading?: string;
  bodyLines: string[];
  imageHint?: string;
  /** Resolved image URL once AI image generation completes; rendered inline in SlideCanvas. */
  imageUrl?: string;
  speakerNotes: string;
  teachingMove?: string;
  /** True for reveal/answer slides — can be hidden based on answerReveal setting */
  isAnswerSlide?: boolean;
  /**
   * Traceability back to the source LessonBlueprint item.
   * section = 'guidedPractice' | 'directInstruction' | 'independentPractice' | etc.
   * n = 1-based position within that section.
   * Undefined for structural slides (title, hook, engagement).
   */
  blueprintRef?: { section: string; n: number };
  /**
   * Provenance of any graphic on this slide.  Stored for internal tracking; never shown to students.
   */
  _graphicsProvenance?: { source: 'ai-hint' | 'teacher-upload'; prompt?: string };
}

// ── Generated presentation ────────────────────────────────────────────────────

export interface GeneratedPresentation {
  title: string;
  grade: string;
  gradeLabel: string;
  subject: string;
  standard: string;
  config: PresentationConfig;
  slides: PresentationSlide[];
  generatedAt: string;
  /**
   * Content-stable key derived from the source lesson's instructional sections.
   * Changes whenever the lesson is regenerated. Used by the UI to warn "lesson
   * was updated — regenerate presentation to stay in sync."
   */
  syncKey?: string;
  /**
   * Slide IDs the teacher has chosen to hide.
   * Hidden slides are dimmed in the strip, excluded from fullscreen presentation and PPTX export.
   * Hiding never removes the slide from the data structure.
   */
  hiddenSlides?: string[];
  /**
   * Persisted school branding settings.  Stored as a generic record to avoid cross-module
   * import of BrandingData; cast to BrandingData in the component layer.
   */
  branding?: Record<string, unknown>;
}

// ── Theme token map ───────────────────────────────────────────────────────────

export interface SlideTheme {
  bg: string;           // slide background class
  heading: string;      // heading text color class
  body: string;         // body text color class
  accent: string;       // accent/highlight color class
  accentBg: string;     // accent background class
  badge: string;        // slide-type badge class
  border: string;       // border color class
  overlay: string;      // semi-transparent overlay for image slides
  pptxBg: string;       // hex for PPTX background
  pptxHeading: string;  // hex for PPTX heading
  pptxBody: string;     // hex for PPTX body
}

export const SLIDE_THEMES: Record<VisualStyle, SlideTheme> = {
  minimal: {
    bg: 'bg-white',
    heading: 'text-slate-900',
    body: 'text-slate-600',
    accent: 'text-slate-800',
    accentBg: 'bg-slate-100',
    badge: 'bg-slate-200 text-slate-600',
    border: 'border-slate-200',
    overlay: 'bg-white/90',
    pptxBg: 'FFFFFF',
    pptxHeading: '1e293b',
    pptxBody: '475569',
  },
  modern: {
    bg: 'bg-white',
    heading: 'text-slate-900',
    body: 'text-slate-600',
    accent: 'text-blue-600',
    accentBg: 'bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
    border: 'border-blue-200',
    overlay: 'bg-white/90',
    pptxBg: 'FFFFFF',
    pptxHeading: '1e293b',
    pptxBody: '2563eb',
  },
  elementary: {
    bg: 'bg-amber-50',
    heading: 'text-orange-800',
    body: 'text-amber-900',
    accent: 'text-orange-500',
    accentBg: 'bg-orange-100',
    badge: 'bg-orange-200 text-orange-700',
    border: 'border-orange-200',
    overlay: 'bg-amber-50/90',
    pptxBg: 'fffbeb',
    pptxHeading: '9a3412',
    pptxBody: '92400e',
  },
  'middle-school': {
    bg: 'bg-violet-50',
    heading: 'text-violet-900',
    body: 'text-violet-700',
    accent: 'text-violet-600',
    accentBg: 'bg-violet-100',
    badge: 'bg-violet-200 text-violet-700',
    border: 'border-violet-200',
    overlay: 'bg-violet-50/90',
    pptxBg: 'f5f3ff',
    pptxHeading: '4c1d95',
    pptxBody: '5b21b6',
  },
  'high-school': {
    bg: 'bg-slate-50',
    heading: 'text-indigo-900',
    body: 'text-slate-600',
    accent: 'text-indigo-600',
    accentBg: 'bg-indigo-50',
    badge: 'bg-indigo-100 text-indigo-700',
    border: 'border-indigo-200',
    overlay: 'bg-slate-50/90',
    pptxBg: 'f8fafc',
    pptxHeading: '1e1b4b',
    pptxBody: '4338ca',
  },
  colorful: {
    bg: 'bg-gradient-to-br from-blue-500 to-violet-600',
    heading: 'text-white',
    body: 'text-blue-100',
    accent: 'text-yellow-300',
    accentBg: 'bg-white/20',
    badge: 'bg-white/30 text-white',
    border: 'border-white/30',
    overlay: 'bg-blue-500/90',
    pptxBg: '3b82f6',
    pptxHeading: 'ffffff',
    pptxBody: 'bfdbfe',
  },
  dark: {
    bg: 'bg-slate-900',
    heading: 'text-white',
    body: 'text-slate-300',
    accent: 'text-cyan-400',
    accentBg: 'bg-slate-800',
    badge: 'bg-slate-700 text-slate-300',
    border: 'border-slate-700',
    overlay: 'bg-slate-900/90',
    pptxBg: '0f172a',
    pptxHeading: 'ffffff',
    pptxBody: '94a3b8',
  },
};

// Slide kind → label for display
export const SLIDE_KIND_LABELS: Record<SlideKind, string> = {
  'title': 'Title',
  'learning-target': 'Learning Target',
  'hook': 'Hook',
  'vocabulary': 'Vocabulary',
  'concept': 'Concept',
  'prediction': 'Prediction',
  'discuss': 'Discuss',
  'reveal': 'Reveal',
  'misconception': 'Common Mistake',
  'teacher-tip': 'Teacher Tip',
  'practice': 'Guided Practice',
  'you-try': 'You Try',
  'engagement': 'Engagement',
  'exit-ticket': 'Exit Ticket',
};
